/**
 * GA4-Einwilligung: Portal nach document.body, echter Modal-Charakter —
 * Klick auf den dunklen Bereich schließt nicht (nur Akzeptieren / Ablehnen).
 * Über reopenSignal erneut öffenbar (Cookie-Einstellungen).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, SpaceBetween } from '@cloudscape-design/components';

import { DATENSCHUTZ_URL } from '@/utils/constants';
import {
  getStoredAnalyticsConsent,
  isAnalyticsEnabled,
  recordAnalyticsConsentAndActivate,
} from '@/utils/analytics';

export type AnalyticsConsentModalProps = {
  /** Bei jedem Inkrement wird das Modal geöffnet (Cookie-Einstellungen). */
  reopenSignal?: number;
};

const AnalyticsConsentModal: React.FC<AnalyticsConsentModalProps> = ({ reopenSignal = 0 }) => {
  const [visible, setVisible] = useState(() => {
    if (!isAnalyticsEnabled()) {
      return false;
    }
    return getStoredAnalyticsConsent() === null;
  });

  useEffect(() => {
    if (reopenSignal > 0 && isAnalyticsEnabled()) {
      setVisible(true);
    }
  }, [reopenSignal]);

  const accept = useCallback(() => {
    recordAnalyticsConsentAndActivate('granted');
    setVisible(false);
  }, []);

  const decline = useCallback(() => {
    recordAnalyticsConsentAndActivate('denied');
    setVisible(false);
  }, []);

  useEffect(() => {
    if (!visible) {
      return;
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [visible]);

  if (!isAnalyticsEnabled() || !visible || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="app-analytics-consent-overlay" role="presentation">
      <div className="app-analytics-consent-backdrop" aria-hidden="true" />
      <div
        className="app-analytics-consent-dialog-card app-analytics-consent-dialog-card-floating"
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-analytics-consent-title"
      >
        <div className="app-analytics-consent-dialog-header">
          <span className="app-analytics-consent-dialog-icon" aria-hidden="true">
            i
          </span>
          <h2 id="app-analytics-consent-title" className="app-analytics-consent-dialog-title">
            Nutzungsanalyse (optional)
          </h2>
        </div>
        <Box className="app-analytics-consent-panel" padding={{ horizontal: 'l', vertical: 'l' }}>
          <SpaceBetween direction="vertical" size="m">
            <Box variant="p">
              Ich setze Google Analytics 4 ein, um zu verstehen, wie diese App genutzt wird (z. B.
              aufgerufene Bereiche und grobe Nutzungswege). Die Auswertung erfolgt mit pseudonymen
              Kennungen; es werden keine Inhalte aus Parqet, keine Portfolio- oder Steuerdaten und
              keine Finanzinformationen aus der App an Google übermittelt.
            </Box>
            <Box variant="p">
              Rechtsgrundlage ist Ihre Einwilligung (Art. 6 Abs. 1 lit. a DSGVO i. V. m. § 25 Abs. 1
              TTDSG). Die Einwilligung ist freiwillig; bei Ablehnung wird kein Analyse-Skript
              geladen. Sie können sie jederzeit mit Wirkung für die Zukunft über Cookie-Einstellungen
              im Fußbereich der App widerrufen.
            </Box>
            <Box variant="p">
              Anbieter ist die Google Ireland Limited; je nach Verarbeitung kann eine Übermittlung
              in die USA erfolgen. Einzelheiten zu Zweck, Speicherdauer und Ihren Rechten finden Sie
              unter{' '}
              <a
                href={DATENSCHUTZ_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="app-analytics-consent-legal-link"
              >
                Datenschutz
              </a>
              .
            </Box>
          </SpaceBetween>
        </Box>
        <div className="app-analytics-consent-dialog-footer">
          <div className="app-analytics-consent-footer">
            <button type="button" className="app-analytics-consent-decline" onClick={decline}>
              Ablehnen
            </button>
            <button type="button" className="app-analytics-consent-accept" onClick={accept}>
              Akzeptieren
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AnalyticsConsentModal;
